import {Editor} from '@tiptap/react';
import React from 'react';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import TextEditorInput from '../../../../../incase-domain/components/textEditor/TextEditorInput';
import useFontOptions from '../../../../../hooks/useFontOptions';
import {editorModeSelector} from '../../../../../store/selectors/tiptapEditor.selectors';
import {EditorModeEnum} from '../../../../../store/actions/types/tiptapEditor.types';
import DropdownMenuCompact, {
    DropdownMenuCompactMenuPosition,
} from '../../../../../incase-domain/components/dropdownMenuCompact/DropdownMenuCompact';
import Button from '../../../../../incase-domain/components/button/Button';
import Label from '../../../../../incase-domain/components/label/Label';

interface FontToolbarProps {
    editor: Editor;
}

function FontToolbar({editor}: FontToolbarProps): JSX.Element {
    const {t} = useTranslation();

    const fontOptions = useFontOptions();
    const editorMode = useSelector(editorModeSelector);

    const lineHeight = editor.getAttributes('paragraph').lineHeight || editor.getAttributes('heading').lineHeight;
    const {fontSize} = editor.getAttributes('textStyle');

    const pxToEm = (px: number): number => px / 16;
    const emToPx = (em: string): number => parseFloat(em) * 16;

    const isSelectMode = editorMode === EditorModeEnum.SELECT;

    const getCurrentFontSize = (): number => {
        if (!fontSize) return 16;
        if (fontSize.includes('em')) {
            return emToPx(fontSize);
        }
        if (fontSize.includes('px')) {
            return parseFloat(fontSize);
        }
        return 16;
    };

    return (
        <div className="u-flex u-flex-col u-gap-2">
            <div className="u-flex u-gap-2">
                <div>
                    <Label>{t('designSidepanel.fontSize')}</Label>
                    <TextEditorInput
                        onChange={(sizePx) => {
                            const sizeEm = pxToEm(sizePx);
                            if (isSelectMode) {
                                editor.chain().selectAll().setFontSize(`${sizeEm}em`).run();
                            } else {
                                editor.commands.setFontSize(`${sizeEm}em`);
                            }
                        }}
                        defaultValue={getCurrentFontSize()}
                        max={92}
                    />
                </div>
                <div>
                    <Label>{t('designSidepanel.lineHeight')}</Label>
                    <TextEditorInput
                        onChange={(lh) => {
                            if (isSelectMode) {
                                editor.chain().selectAll().setLineHeight(lh).run();
                            } else {
                                editor.commands.setLineHeight(lh);
                            }
                        }}
                        defaultValue={lineHeight || 1.48}
                        max={3}
                        step={0.1}
                    />
                </div>
            </div>

            <DropdownMenuCompact
                menuPosition={DropdownMenuCompactMenuPosition.DOWN}
                width="full"
                items={[
                    fontOptions.map((el) => ({
                        label: el.label,
                        font: {
                            name: el.label.toString(),
                            url: el.value.toString(),
                        },
                        onClick: () => {
                            if (el && el.label) {
                                if (isSelectMode) {
                                    editor.chain().selectAll().setFontFamily(el.label.toString()).run();
                                } else {
                                    editor.commands.setFontFamily(el.label.toString());
                                }
                            }
                        },
                    })),
                ]}
            >
                <Button className="c-sidepanel__dropdown_button" trailing="solar:alt-arrow-down-linear" size="xs" variant="soft" fullWidth>
                    {fontOptions.find((option) => option.label === editor.getAttributes('textStyle').fontFamily)?.label || 'Roboto'}
                </Button>
            </DropdownMenuCompact>
        </div>
    );
}

export default FontToolbar;
